import { useEffect, useRef, useState } from 'react';
import { CategoryPill } from '../CategoryPill';
import { useWheelDragScroll } from '../useWheelDragScroll';

export interface CategoryPillItem {
  id: string;
  label: string;
  iconSrc?: string;
}

export interface CategoryPillRowProps {
  categories: CategoryPillItem[];
  activeId?: string;
  onSelect?: (id: string) => void;
  /**
   * When true (default), content is centred as soon as the row's own
   * container is ≥ 780px wide. Pass `false` for contexts that should
   * always start from the left edge (e.g. under a mobile hero).
   */
  centered?: boolean;
  className?: string;
}

type FadeState = 'none' | 'left' | 'right' | 'both';

function fadeClassFor(state: FadeState): string {
  if (state === 'both') return 'fade-edge-both';
  if (state === 'left') return 'fade-edge-l';
  if (state === 'right') return 'fade-edge-r';
  return '';
}

/**
 * Horizontal scrolling row of CategoryPills with:
 *   • hidden scrollbar (Chrome/Safari/Firefox/IE)
 *   • mask fade on whichever edge still has scrollable content
 *   • mouse wheel and drag scrolling, so a desktop can reach every pill
 *   • optional auto-centring when the row has room for everything
 *
 * Reused by Header (centred on desktop) and by the mobile
 * "pills-under-hero" layout on the pack page (left-aligned).
 */
export function CategoryPillRow({
  categories,
  activeId,
  onSelect,
  centered = true,
  className = '',
}: CategoryPillRowProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState<FadeState>('none');
  useWheelDragScroll(scrollRef, categories.length);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const canLeft = el.scrollLeft > 2;
      const canRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
      setFade(
        canLeft && canRight
          ? 'both'
          : canLeft
            ? 'left'
            : canRight
              ? 'right'
              : 'none',
      );
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [categories.length]);

  const centerClass = centered ? '@[780px]:justify-center' : '';

  return (
    <nav
      aria-label="Sport categories"
      className={`min-w-0 @container ${className}`.trim()}
    >
      <div
        ref={scrollRef}
        className={`scrollbar-hide ${fadeClassFor(fade)} flex items-center gap-2 overflow-x-auto ${centerClass}`.trim()}
      >
        {categories.map((c, idx) => (
          // `${id}-${idx}` — backend can return two categories with the
          // same representative pool_offer_id (one offer belongs to several
          // sports via PropertyOption), and React requires unique keys.
          <CategoryPill
            key={`${c.id}-${idx}`}
            label={c.label}
            {...(c.iconSrc !== undefined ? { iconSrc: c.iconSrc } : {})}
            active={c.id === activeId}
            onClick={() => onSelect?.(c.id)}
            className="shrink-0"
          />
        ))}
      </div>
    </nav>
  );
}
